import { MenuItem, TextField as TextFieldMui } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Spacing } from '../../design-system/spacing';
import { Kind } from '../../types/kind';
import { styled } from '../../utils/styled';
import { Box } from '../Box';
import { IconButton } from '../IconButton';

export const MainColorIcon = styled(IconButton)`
  color: ${({ theme }) => theme.palette.primary.main};
  &:hover {
    color: ${({ theme }) => theme.palette.primary.dark};
  }
`;

export const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'lowerCase',
})<{ lowerCase?: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 10px;
  padding: ${({ theme }) => theme.spacing(Spacing.Medium)};
  text-transform: ${({ lowerCase }) => (lowerCase ? 'none' : 'capitalize')};
  &.Mui-selected {
    background-color: unset;
    font-weight: 700;
    color: ${({ theme }) => theme.palette.primary.main};
  }
  &.Mui-selected:hover {
    background-color: ${({ theme }) => theme.palette.neutral[100]};
  }
`;

export const StyledMenuItemContent = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding-right: ${({ theme }) => theme.spacing(Spacing.Medium)};
`;

export const StyledErrorContainer = styled('div')`
  height: ${({ theme }) => theme.spacing(Spacing.Medium)};
`;

export const StyledIconWrapper = styled('div')<{
  side?: 'left' | 'right';
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${({ side, theme }) =>
    side === 'left' ? theme.spacing(Spacing.Small) : 0};
  margin-left: ${({ side, theme }) =>
    side === 'right' ? theme.spacing(Spacing.Small) : 0};
`;

export const StyledField = styled(TextFieldMui, {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind?: Kind; error?: boolean }>`
  background-color: transparent;
  color: ${({ theme, kind }) =>
    kind === 'dark'
      ? theme.palette.text.primaryInverted
      : theme.palette.text.primary};

  .MuiInputBase-root {
    background-color: ${({ theme }) => theme.palette.background.paper};
  }

  .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-radius: ${({ theme }) => theme.borderRadii.outer};
    border-color: ${({ theme, kind, error }) => {
      if (error) return theme.palette.error.main;
      if (kind === 'dark') return theme.palette.neutral[800];
      return theme.palette.neutral[200];
    }};
  }
  //Style for the arrow at the right
  :hover .MuiSelect-icon input:not(:active) {
    color: ${({ theme }) => theme.palette.primary.main};
  }
  .MuiSelect-icon {
    color: ${({ theme, error }) =>
      error ? theme.palette.error.main : theme.palette.primary.main};
    right: ${({ theme }) => theme.spacing(Spacing.Medium)};
    top: calc(50% - 0.7em);
  }
  .MuiSelect-icon.Mui-disabled {
    color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[600]
        : theme.palette.neutral[300]};
  }
  //Styles for the Icon at the left
  .MuiInputBase-adornedStart {
    color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[600]
        : theme.palette.neutral[400]};
  }
  .MuiInputBase-adornedStart.Mui-disabled {
    color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[600]
        : theme.palette.neutral[300]};
  }
  //Style for the Label on hover
  :hover .MuiInputLabel-root input:not(:active) {
    color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.text.primaryInverted
        : theme.palette.text.primary};
  }
  //Style for the Label
  .MuiInputLabel-root.Mui-disabled {
    color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[800]
        : theme.palette.neutral[300]};
  }
  //State hover for changing the background color
  :hover .MuiInputBase-root input:not(:active) {
    background-color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[900]
        : theme.palette.neutral[100]};
    border-radius: ${({ theme }) => theme.borderRadii.outer};
  }
  //State hover for changing the background color when disabled
  :hover .MuiInputBase-root.Mui-disabled input:not(:active) {
    background-color: unset;
    border-color: unset;
  }
  :hover .MuiInputBase-root.Mui-disabled {
    border-color: unset;
    background-color: unset;
  }
  //Value Color
  .MuiInputBase-input {
    color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.text.primaryInverted
        : theme.palette.text.primary};
  }
  //Value color
  .MuiInputBase-root-MuiOutlinedInput-root {
    color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[200]
        : theme.palette.neutral[300]};
  }
  //Override the border color of the Input when Disabled
  .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[800]
        : theme.palette.neutral[200]};
  }
  //Override the border color of the Input on hover
  :hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme, kind, error }) => {
      if (error) return theme.palette.error.main;
      if (kind === 'dark') return theme.palette.neutral[800];
      return theme.palette.neutral[200];
    }};
  }
  :hover .MuiOutlinedInput-root {
    background-color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[800]
        : theme.palette.neutral[100]};
    border-radius: ${({ theme }) => theme.borderRadii.outer};
  }
  //Color for the value of the select without the Icon
  //Color for the value of the select without the Icon Disabled
  .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled {
    -webkit-text-fill-color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[800]
        : theme.palette.neutral[300]};
  }
  //Color for the value of the select without the Icon Disabled
  .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled {
    -webkit-text-fill-color: ${({ theme, kind }) =>
      kind === 'dark'
        ? theme.palette.neutral[800]
        : theme.palette.neutral[300]};
  }
  .MuiSelect-select {
    color: ${({ theme, kind, error }) => {
      if (error) return theme.palette.error.main;
      if (kind === 'dark') return theme.palette.text.primaryInverted;
      return theme.palette.text.primary;
    }};
    margin-left: ${({ theme }) => theme.spacing(Spacing.Tiny)};
  }
  .MuiInputAdornment-root {
    width: 40px;
  }
`;

export const StyledDatePickerField = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== 'error',
})<{ error?: boolean; kind?: Kind }>`
  background-color: ${({ theme }) => theme.palette.background.paper};

  .MuiPickersOutlinedInput-notchedOutline {
    border-radius: ${({ theme }) => theme.borderRadii.outer};
    border-color: ${({ theme, error }) =>
      error ? theme.palette.error.main : theme.palette.neutral[200]};
  }
  //Styles for the Icon at the left
  .MuiInputBase-adornedStart {
    color: ${({ theme }) => theme.palette.neutral[400]};
  }
  .MuiInputBase-adornedStart.Mui-disabled {
    color: ${({ theme }) => theme.palette.neutral[300]};
  }
  //Style for the Label on hover
  :hover .MuiInputLabel-root input:not(:active) {
    color: ${({ theme }) => theme.palette.text.primary};
  }
  //Style for the Label
  .MuiInputLabel-root.Mui-disabled {
    color: ${({ theme }) => theme.palette.neutral[300]};
  }
  //State hover for changing the background color
  :hover .MuiInputBase-root input:not(:active) {
    background-color: ${({ theme }) => theme.palette.neutral[100]};
    border-radius: ${({ theme }) => theme.borderRadii.outer};
  }
  //State hover for changing the background color when disabled
  :hover .MuiInputBase-root.Mui-disabled input:not(:active) {
    background-color: unset;
    border-color: unset;
  }
  :hover .MuiInputBase-root.Mui-disabled {
    border-color: unset;
    background-color: unset;
  }
  //Value Color
  .MuiInputBase-input {
    color: ${({ theme }) => theme.palette.text.primary};
  }
  //Value color
  .MuiInputBase-root-MuiOutlinedInput-root {
    color: ${({ theme }) => theme.palette.neutral[300]};
  }
  //Override the border color of the Input when Disabled
  .MuiPickersOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.palette.neutral[200]};
  }
  //Override the border color of the Input on hover
  :hover .MuiPickersOutlinedInput-root .MuiPickersOutlinedInput-notchedOutline {
    border-color: ${({ theme, error }) =>
      error ? theme.palette.error.main : theme.palette.neutral[200]};
  }
  :hover .MuiPickersOutlinedInput-root {
    background-color: ${({ theme }) => theme.palette.neutral[100]};
    border-radius: ${({ theme }) => theme.borderRadii.outer};
  }
  //Color for the value of the select without the Icon Disabled
  .MuiInputBase-input.MuiPickersOutlinedInput-input.Mui-disabled {
    -webkit-text-fill-color: ${({ theme }) => theme.palette.neutral[300]};
  }
  .MuiInputAdornment-root {
    width: 40px;
  }
`;

export const StyledBoxWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(Spacing.Medium)};
  width: 100%;
  min-width: 0;
`;

export const StyledChipWrapperBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: auto;
  flex-shrink: 0;
`;

export const StyledListSubheaderContent = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(Spacing.Medium)};
  margin-bottom: ${({ theme }) => theme.spacing(Spacing.Medium)};
  width: 100%;
`;

export const StyledListSubheaderBox = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(Spacing.Medium)};
`;

export const StyledTooltipBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(Spacing.Medium)};
`;

export const StyledGroupDividerWrapper = styled(Box)`
  margin: ${({ theme }) => theme.spacing(Spacing.Medium)} 0;
  width: 100%;
`;

export const StyledSelectedOptionBox = styled(Box)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
